import { Component, OnInit, OnDestroy } from '@angular/core';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import { FeatureCodes, UserPreferenceType } from '@piqet-angular/shared';
import {
  FeatureConfig,
  FeatureConfigService,
} from 'libs/shared/src/lib/service/feature-config.service';
import { CookieHelperService } from 'libs/shared/src/lib/service/cookie-helper.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

export interface PremiumHeaderParams extends IHeaderParams {
  isPremiumFeature?: boolean;
  featureEnabled?: boolean;
  featureCode?: FeatureCodes;
}

@Component({
  selector: 'app-premium-header',
  template: `
    <div class="ag-cell-label-container" role="presentation">
      <span
        class="crown-icon-wrapper"
        *ngIf="showCrownIcon"
        (mouseenter)="onMouseEnter()"
        (mouseleave)="onMouseLeave()"
      >
        <svg
          class="crown-icon"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <radialGradient [id]="gradientId">
              <stop offset="0%" style="stop-color: #9747FF" />
              <stop offset="100%" style="stop-color: #9747FF" />
            </radialGradient>
          </defs>
          <path
            d="M4 16L2 6l5 3 2-5h6l2 5 5-3-2 10H4z"
            [attr.fill]="'url(#' + gradientId + ')'"
            stroke="#9747FF"
            stroke-width="1"
          />
          <rect
            x="3"
            y="16"
            width="18"
            height="4"
            rx="1"
            [attr.fill]="'url(#' + gradientId + ')'"
          />
          <circle cx="12" cy="12" r="2" fill="#FFF" opacity="0.9" />
          <circle cx="7" cy="13" r="1.5" fill="#FFF" opacity="0.9" />
          <circle cx="17" cy="13" r="1.5" fill="#FFF" opacity="0.9" />
        </svg>

        <!-- Premium Popup -->
        <div
          class="premium-popup"
          *ngIf="showPopup && !globalHidePrompt"
          (mouseenter)="onPopupMouseEnter()"
          (mouseleave)="onPopupMouseLeave()"
        >
          <div class="popup-header">
            <svg
              class="popup-crown-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <radialGradient [id]="popupGradientId">
                  <stop offset="0%" style="stop-color: #9747FF" />
                  <stop offset="100%" style="stop-color: #9747FF" />
                </radialGradient>
              </defs>
              <path
                d="M4 16L2 6l5 3 2-5h6l2 5 5-3-2 10H4z"
                [attr.fill]="'url(#' + popupGradientId + ')'"
                stroke="#9747FF"
                stroke-width="1"
              />
              <rect
                x="3"
                y="16"
                width="18"
                height="4"
                rx="1"
                [attr.fill]="'url(#' + popupGradientId + ')'"
              />
              <circle cx="12" cy="12" r="2" fill="#FFF" opacity="0.9" />
              <circle cx="7" cy="13" r="1.5" fill="#FFF" opacity="0.9" />
              <circle cx="17" cy="13" r="1.5" fill="#FFF" opacity="0.9" />
            </svg>
            <span class="popup-title">Premium Feature</span>
          </div>
          <p class="popup-description">
            {{
              config?.compactDescription ||
                config?.description ||
                'Upgrade to Organizational or Enterprise license.'
            }}
          </p>
          <div class="popup-actions">
            <button class="popup-upgrade-btn" (click)="onUpgradeClick()">Upgrade</button>
          </div>
          <div class="checkbox-container">
            <label class="hide-prompt-checkbox">
              <input
                type="checkbox"
                [(ngModel)]="hidePromptChecked"
                (change)="onHidePromptChange()"
              />
              Don't show again for any features
            </label>
          </div>
        </div>
      </span>
      <span class="ag-header-cell-text">{{ params.displayName }}</span>
    </div>
  `,
  styles: [
    `
      .ag-cell-label-container {
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        height: 100%;
      }

      .ag-header-cell-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .crown-icon-wrapper {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        cursor: pointer;
        position: relative;
      }

      .crown-icon {
        display: block;
      }

      .premium-popup {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        margin-top: 8px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        padding: 16px;
        min-width: 250px;
        z-index: 1000;
        text-align: left;
      }

      .premium-popup::before {
        content: '';
        position: absolute;
        top: -6px;
        left: 50%;
        transform: translateX(-50%);
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid white;
      }

      .popup-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }

      .popup-crown-icon {
        display: block;
      }

      .popup-title {
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }

      .popup-description {
        font-size: 13px;
        color: #666;
        margin: 0 0 12px 0;
        line-height: 1.4;
      }

      .popup-actions {
        margin-bottom: 12px;
      }

      .popup-upgrade-btn {
        width: 100%;
        padding: 10px 16px;
        background: linear-gradient(135deg, #9747ff 0%, #7928ca 100%);
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s;
      }

      .popup-upgrade-btn:hover {
        opacity: 0.9;
      }

      .checkbox-container {
        border-top: 1px solid #eee;
        padding-top: 12px;
      }

      .hide-prompt-checkbox {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: #666;
        cursor: pointer;
      }

      .hide-prompt-checkbox input {
        width: 14px;
        height: 14px;
        cursor: pointer;
      }
    `,
  ],
})
export class PremiumHeaderComponent implements IHeaderAngularComp, OnInit, OnDestroy {
  params!: PremiumHeaderParams;
  showCrownIcon = false;
  showPopup = false;
  hidePromptChecked = false;
  globalHidePrompt = false;
  config: FeatureConfig | null = null;

  gradientId = `gradient-header-${Math.random().toString(36).substr(2, 9)}`;
  popupGradientId = `gradient-popup-${Math.random().toString(36).substr(2, 9)}`;

  private destroy$ = new Subject<void>();
  private hidePopupTimeout: any;
  private isHoveringIcon = false;
  private isHoveringPopup = false;

  constructor(
    private featureConfigService: FeatureConfigService,
    private cookieHelperService: CookieHelperService,
    private toaster: ToastrService
  ) {}

  ngOnInit(): void {
    this.globalHidePrompt = this.getGlobalPromptPreference();

    this.featureConfigService
      .getGlobalHidePromptObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: boolean) => {
        this.globalHidePrompt = val;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.hidePopupTimeout) {
      clearTimeout(this.hidePopupTimeout);
    }
  }

  agInit(params: PremiumHeaderParams): void {
    this.params = params;
    this.showCrownIcon = params.isPremiumFeature === true && params.featureEnabled === false;

    if (params.featureCode) {
      this.config = this.featureConfigService.getFeatureConfig(params.featureCode);
    }
  }

  refresh(params: PremiumHeaderParams): boolean {
    this.params = params;
    this.showCrownIcon = params.isPremiumFeature === true && params.featureEnabled === false;

    if (params.featureCode) {
      this.config = this.featureConfigService.getFeatureConfig(params.featureCode);
    }
    return true;
  }

  onMouseEnter(): void {
    this.isHoveringIcon = true;
    this.clearHideTimeout();
    this.showPopup = true;
  }

  onMouseLeave(): void {
    this.isHoveringIcon = false;
    this.scheduleHidePopup();
  }

  onPopupMouseEnter(): void {
    this.isHoveringPopup = true;
    this.clearHideTimeout();
  }

  onPopupMouseLeave(): void {
    this.isHoveringPopup = false;
    this.scheduleHidePopup();
  }

  private scheduleHidePopup(): void {
    this.clearHideTimeout();
    if (!this.isHoveringIcon && !this.isHoveringPopup) {
      this.hidePopupTimeout = setTimeout(() => {
        if (!this.isHoveringIcon && !this.isHoveringPopup) {
          this.showPopup = false;
        }
      }, 300);
    }
  }

  private clearHideTimeout(): void {
    if (this.hidePopupTimeout) {
      clearTimeout(this.hidePopupTimeout);
      this.hidePopupTimeout = null;
    }
  }

  onUpgradeClick(): void {
    window.open(
      'https://piqet.com/sustainable-packaging-lca-calculator#license-pricelists',
      '_blank'
    );
  }

  onHidePromptChange(): void {
    this.featureConfigService
      .saveUserPreference(UserPreferenceType.UpgradePlanPopup, this.hidePromptChecked)
      .then((res: any) => {
        if (res.data) {
          this.toaster.success('Popup preferences updated for all features');
          this.setGlobalPromptPreference();
          this.globalHidePrompt = this.hidePromptChecked;
          this.featureConfigService.setHidePromptGlobally(this.hidePromptChecked);
        } else {
          this.toaster.error(res.message);
          this.hidePromptChecked = false;
        }
      })
      .catch(error => {
        console.error('Failed to save preference:', error);
        this.toaster.error('Failed to save preference');
        this.hidePromptChecked = false;
      });
  }

  private getGlobalPromptPreference(): boolean {
    const globalPref = this.cookieHelperService.getGlobalUpgradePopupPreference();
    return globalPref === 'true';
  }

  private setGlobalPromptPreference(): void {
    const oneYearFromNow = new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000);
    this.cookieHelperService.setGlobalUpgradePopupPreference(
      this.hidePromptChecked,
      oneYearFromNow
    );
  }
}
